import type Firebase from 'firebase/remote-config'
import { fetchAndActivate, getRemoteConfig, getValue } from 'firebase/remote-config'
import { store } from '../../app/store'
import env from '../../env.json'
import { firebase } from '../../firebase'
import { getFlagOverrides } from 'features/devTools/flagOverrides'
import { defaultFlags } from './defaultFlags'
import { SET_FLAGS } from './FlagsConstants'

const remoteConfig = getRemoteConfig(firebase)
// A floor on *triggered* Remote Config fetches, not a poll interval — nothing here polls,
// so fetch volume is bounded by navigation, not by this number. 60s exists so a flag flipped
// after a release shows up on the next reload instead of up to an hour later. It sits inside
// the 15–60s band the flag-service ecosystem settled on (Unleash 15s, LaunchDarkly 30s
// polling, ConfigCat 60s); Firebase's 12h default targets battery-bound mobile, not web.
const PRODUCTION_FETCH_INTERVAL_MS = 60_000
const NON_PRODUCTION_FETCH_INTERVAL_MS = 0

remoteConfig.settings.minimumFetchIntervalMillis =
  env.name === 'Production' ? PRODUCTION_FETCH_INTERVAL_MS : NON_PRODUCTION_FETCH_INTERVAL_MS

// @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
function setFlags(payload) {
  const action = {
    type: SET_FLAGS,
    payload,
  }
  return action
}

const flagPrefix = {
  DEV: 'dev_',
  LOCAL: 'dev_',
  QA: 'qa_',
  legolas: 'qa_',
  gimli: 'qa_',
  boromir: 'qa_',
  pippin: 'qa_',
  merry: 'qa_',
  UAT: 'qa_',
  Autotest: 'qa_',
  PreProd: 'preprod_',
  Production: 'prod_',
}

// `env.name` is a plain `string` off env.json, so a keyed lookup can never narrow to one of
// these literals. Narrowing first keeps the fallback and drops the suppression the inline
// lookup needed.
//
// `hasOwnProperty.call` rather than `in`, which also answers true for everything on
// `Object.prototype` — `'toString'` would satisfy the predicate and then index to a function.
// `Object.hasOwn` reads better but is an ES2022 *built-in*, which the bundler's target only
// transpiles syntax for, so it would throw on the older Safari the browserslist still covers.
const isKnownEnv = (name: string): name is keyof typeof flagPrefix =>
  Object.prototype.hasOwnProperty.call(flagPrefix, name)

/**
 * The Remote Config parameter name for `key` in this environment.
 *
 * Used by the getters *and* by `defaultConfig`, which the two must agree on: `defaultConfig` is
 * keyed by parameter name, so a default registered under a name nothing reads is simply dead.
 * They disagreed for any environment missing from `flagPrefix` — the read fell back to the QA
 * prefix while the default went under a literal `undefined` one.
 */
const flagName = (key: string) => `${isKnownEnv(env.name) ? flagPrefix[env.name] : flagPrefix.QA}${key}`

const asBoolean = (value: Firebase.Value) => value.asBoolean()
const asJSON = (value: Firebase.Value) => {
  try {
    return JSON.parse(value.asString())
  } catch (error) {
    console.error('Error parsing flag', error)
    return value.asString()
  }
}
const asString = (value: Firebase.Value) => value.asString()
// For a flag that exists only as a Dev Tools pin: whatever the template holds is ignored, so a
// parameter added to Remote Config by mistake cannot switch it on for everyone.
const pinOnly = () => false

const flagParsers = {
  id_verification_show_tag_provider: asBoolean,
  product_order_types: asJSON,
  dts_moud_pilot: asJSON,
  pdmp_pilot: asJSON,
  pdmp_integrated_states: asJSON,
  adyen: asJSON,
  provider_google_sso: asBoolean,
  sentry_feedback_provider: asJSON,
  async_refills_phase_1: asBoolean,
  pharmacy_search_daw: asBoolean,
  daw_show_treat_and_add: asJSON,
  unified_token_refresh: asBoolean,
  program_visit_badge_provider: asBoolean,
  live_visit_hd_mock: pinOnly,
  iterable_announcements_provider: asBoolean,
}

export function setConfigFlags() {
  const flags: Record<string, unknown> = {}
  Object.keys(defaultFlags).forEach((key) => {
    // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
    const parser = flagParsers[key] ?? asString

    flags[key] = parser(getValue(remoteConfig, flagName(key)))
  })

  // Dev Tools pins are applied last so they outrank Remote Config — a flag pinned from the
  // panel would otherwise be silently undone by the next fetch, which is the point of pinning
  // it. Always empty in production, where the override layer is inert.
  return store.dispatch(setFlags({ ...flags, ...getFlagOverrides() }))
}

/**
 * Applies Dev Tools pins to the flags already in the store, without re-reading Remote Config.
 *
 * The panel needs the toggle to land immediately; `setConfigFlags` only runs on a fetch, which
 * could be a long way off. Both paths merge the same override set on top, so the two agree.
 */
export function applyFlagOverrides(overrides: Record<string, unknown>) {
  // The legacy JS reducers give `combineReducers` nothing to infer from, so the slice read here
  // is spelled out rather than indexed off the root state — same reason as `stripInVisitRoom`.
  const state: { flags?: { flags?: Record<string, unknown> } } = store.getState()

  return store.dispatch(setFlags({ ...state.flags?.flags, ...overrides }))
}

export async function getFlags() {
  remoteConfig.defaultConfig = Object.fromEntries(
    Object.entries(defaultFlags).map(([key, value]) => [flagName(key), value]),
  )
  await fetchAndActivate(remoteConfig)
  setConfigFlags()
}
